import {
  CANCEL_CHANGING_PHONE_API,
  CHANGE_PHONE_API,
  CHANGE_PHONE_REQUEST_CONFIRM_API,
  CHECK_PHONE_AVAILABLE_API,
  CHECK_REGISTRY_CODE_API,
  DELETE_USER_API,
  GET_EXTRA_USER_INFO_API,
  GET_USER_INFO_API,
  EDIT_USER_API,
  GET_ORDER_DRAFT_API,
  GET_USER_WITH_TEMPLATES,
  REGISTER_BY_PHONE_API,
  SAVE_ORDER_DRAFT_API,
  SEND_REGISTRY_CODE_API,
  UPDATE_CASHLESS_BILLING_API,
  UPDATE_CASHLESS_INFO_API,
} from '@constants/apiEndpoints'
import { AJAX_REQUEST_HEADERS } from '@constants'
import axios from 'axios'
import getAxiosClient from '@services/axiosClient'
import toCamelcaseKeys from 'camelcase-keys'
import qs from 'qs'
import toSnakecaseKeys from 'snakecase-keys'
import responseSuccessValidator from '@helpers/responseHandlers'
import requestSuccessValidator from '@helpers/requestSuccessValidator'

const axiosClient = getAxiosClient()

class UserService {
  /**
   * @param phone awaiting form object with phone field
   */
  static async checkPhoneAvailable(phone) {
    const response = await axios.post(CHECK_PHONE_AVAILABLE_API, phone, AJAX_REQUEST_HEADERS)

    return response.data
  }

  /**
   * @param data awaiting form object
   * @param data.phoneExistsNeedApprove 1 or 0
   * @param data.phone phone string
   */
  static async sendRegistryCode(data) {
    const response = await axios.post(SEND_REGISTRY_CODE_API, data, AJAX_REQUEST_HEADERS)

    // if (response.status < 0)
    //   throw new Error(response.msg || 'Ошибка при отправке данных пользователя')
    return response.data
  }

  /**
   * @param data awaiting form object
   * @param data.phone phone string
   * @param data.code code string
   */
  static async checkRegistryCode(data) {
    const response = await axios.post(CHECK_REGISTRY_CODE_API, data, AJAX_REQUEST_HEADERS)

    // if (!response.data.success)
    //   throw new Error(response.data.message || 'Ошибка при проверке данных пользователя')
    return response.data
  }

  /**
   * Сохранение черновика
   * @param {object} payload
   * @returns {Promise<Number>} номер черновика
   */
  static async saveDraft(payload) {
    const errMessage = 'Не удалось сохранить черновик'
    const requestData = qs.stringify(payload)
    const axiosClient = axios.create()

    axiosClient.interceptors.response.use(response => {
      const { data } = response

      if (!data) throw Error(errMessage)
      requestSuccessValidator(data, errMessage)

      return response
    })

    const response = await axiosClient.post(SAVE_ORDER_DRAFT_API, requestData, {
      ...AJAX_REQUEST_HEADERS,
      'Access-Control-Allow-Origin': '*',
    })

    return response.data
  }

  /**
   * @param data awaiting form object
   * @param data.tid template id
   */
  static async getDraft(data) {
    const response = await axios.post(GET_ORDER_DRAFT_API, data, {
      ...AJAX_REQUEST_HEADERS,
      'Access-Control-Allow-Origin': '*',
    })

    responseSuccessValidator(response, 'Ошибка при получении шаблона')
    return response.data
  }

  /**
   * @param data awaiting form object
   * @param data.phone phone string
   */
  static async registerByPhone(data) {
    const response = await axios.post(REGISTER_BY_PHONE_API, data, AJAX_REQUEST_HEADERS)

    responseSuccessValidator(response)
    return response.data
  }

  /**
   * @param data awaiting form object
   * @param data.phone phone string
   */
  static async getUserWithTemplates(data) {
    const response = await axios.post(GET_USER_WITH_TEMPLATES, data, AJAX_REQUEST_HEADERS)

    responseSuccessValidator(response)
    return response.data.data
  }

  static async getUserInfo() {
    const response = await axios.get(GET_USER_INFO_API)

    responseSuccessValidator(response)
    return response.data
  }

  static async getExtraUserInfo() {
    const { data } = await axiosClient.get(GET_EXTRA_USER_INFO_API)
    const handledData = { ...data.info }

    if (handledData.cashlessBillingInfo)
      handledData.cashlessBillingInfo = toCamelcaseKeys(JSON.parse(handledData.cashlessBillingInfo))
    if (handledData.cashlessInfo)
      handledData.cashlessInfo = toCamelcaseKeys(JSON.parse(handledData.cashlessInfo))

    return handledData
  }

  static async updateCashlessInfo(payload) {
    const { data } = await axiosClient.post(
      UPDATE_CASHLESS_INFO_API,
      qs.stringify(toSnakecaseKeys(payload))
    )

    return data
  }

  static async updateCashlessBilling(payload) {
    const { data } = await axiosClient.post(
      UPDATE_CASHLESS_BILLING_API,
      qs.stringify(toSnakecaseKeys(payload))
    )

    return data
  }

  static async deleteUser() {
    await axios.get(DELETE_USER_API)
  }

  /**
   * @param data awaiting form object
   * @param data.tid template id
   */
  static async editUser(data) {
    const response = await axios.post(EDIT_USER_API, qs.stringify(data))

    responseSuccessValidator(response)
    return response.data
  }

  /**
   * @param data object
   * @param data.code confirm code
   */
  static async changePhoneConfirmRequest(data) {
    const response = await axiosClient.post(CHANGE_PHONE_REQUEST_CONFIRM_API, data)

    return response.data
  }

  static async cancelChangingPhone() {
    const response = await axiosClient.get(CANCEL_CHANGING_PHONE_API)

    return response.data
  }

  /**
   * Смена телефона
   * @param {string} newPhone новый номер телефона
   * @returns {Promise<void>}
   */
  static async changePhone(newPhone) {
    const response = await axiosClient.post(CHANGE_PHONE_API, { newPhone })

    return response.data
  }
}

export default UserService
